import { SortConfig, VirtualTable } from '@components/ui/VirtualTable';
import { useTranslation } from 'react-i18next';
import { PrintersModel } from '../../models/Printers';
import { DropdownActions } from '../interaction/DropdownActions';
import { Badge } from '@shared/components/ui/badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@shared/utils/utils';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';

interface TableProps {
  printers: PrintersModel[];
  isLoading?: boolean;
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
  total?: number;
  onEdit: (printer: PrintersModel) => void; // ✅ corregido
  onRefresh?: () => void; // ✅ se agrega esto
}

export const Table = ({
  printers,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  onEdit,
  onRefresh,
}: TableProps) => {
  const { t } = useTranslation('printersModule');

  const handleSortingChange = (newSorting: SortConfig | null) => {
    onSortingChange?.(newSorting);
  };

  const handleEdit = (printer: PrintersModel) => {
    onEdit?.(printer);
  };

  const columns = [
    {
      header: t('table.printer'),
      accessorKey: 'custom_name',
      size: 30,
      sortable: true,
      cell: ({ row }: any) => (
        <div className="flex flex-col gap-1">
          <div className="line-clamp-1">{row.original.custom_name || row.original.name}</div>
          <div>
            <Badge variant="outline" className="text-xs font-normal">
              {row.original.id}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      header: t('table.connection'),
      accessorKey: 'status',
      size: 15,
      sortable: true,
      cell: ({ row }: any) => (
        <Badge
          variant="outline"
          className={cn(
            'font-normal border-none',
            row.original.status === 'online' ? '!text-success bg-bg-success' : '!text-error bg-bg-error',
          )}
        >
          <FontAwesomeIcon
            icon={row.original.status === 'online' ? faCircleCheck : faCircleXmark}
            className="mr-1 w-3 h-3"
          />
          {row.original.status === 'online' ? t('status.online') : t('status.offline')}
        </Badge>
      ),
    },
    {
      header: t('table.system_state'),
      accessorKey: 'active',
      size: 20,
      sortable: true,
      cell: ({ row }: any) => (
        <span className="text-sm font-medium">{row.original.active ? t('status.enabled') : t('status.disabled')}</span>
      ),
    },
    {
      header: t('table.computer'),
      accessorKey: 'computer',
      size: 20,
      sortable: true,
      cell: ({ row }: any) => <span className="text-sm font-medium">{row.original.computer}</span>,
    },
    {
      header: '',
      accessorKey: 'actions',
      size: 15,
      cell: ({ row }: any) => <DropdownActions printers={row.original} onEdit={handleEdit} onRefresh={onRefresh} />,
    },
  ];

  return (
    <div className="w-full">
      <VirtualTable
        data={printers}
        columns={columns}
        height="calc(100vh - 280px)"
        minHeight="240px"
        rowHeight={70}
        emptyMessage={t('empty.message')}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={200}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        total={total}
      />
    </div>
  );
};
